'use client';
import { Link } from '@/i18n/routing';
import { useHoverPrefetch } from '@/shared/hooks/use-hover-prefetch';
import { cn } from '@ecom/helper-react-class-combine';
import { AnchorHTMLAttributes, PropsWithChildren } from 'react';

// Not Next routes: no locale prefix and nothing to prefetch.
const EXTERNAL_HREF = /^(https?:|mailto:|tel:)/i;

type TextNavLinkProps = {
  href: string;
  onClick?: () => void;
  prefetch?: boolean;
  prefetchOnHover?: boolean;
  className?: string;
  ariaLabel?: string;
  isUnderline?: boolean;
  isBold?: boolean;
  target?: '_self' | '_blank' | '_parent' | '_top';
  rel?: string;
  unlocalized?: boolean;
};
export const TextNavLink = ({
  href,
  children,
  onClick,
  className,
  ariaLabel,
  isUnderline = false,
  isBold = false,
  prefetch,
  prefetchOnHover = false,
  unlocalized = false,
  target,
  rel,
  onMouseEnter,
  ...rest
}: PropsWithChildren<
  TextNavLinkProps & Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href' | 'popover'>
>) => {
  const hoverPrefetch = useHoverPrefetch({ enabled: prefetchOnHover, prefetch, onMouseEnter });
  const linkClassName = cn(
    'text-m transition-colors',
    'focus-visible:ring-primary lg:hover:text-text-hover focus:outline-hidden focus-visible:ring-2 focus-visible:ring-offset-2',
    isUnderline ? 'underline' : '',
    isBold ? 'font-semibold' : '',
    className,
  );

  if (unlocalized || EXTERNAL_HREF.test(href)) {
    return (
      <a
        href={href}
        className={linkClassName}
        onClick={onClick}
        aria-label={ariaLabel}
        target={target}
        rel={rel}
        onMouseEnter={onMouseEnter}
        {...rest}
      >
        {children}
      </a>
    );
  }

  return (
    <Link
      href={href}
      prefetch={hoverPrefetch.prefetch}
      className={linkClassName}
      onClick={onClick}
      onMouseEnter={hoverPrefetch.onMouseEnter}
      aria-label={ariaLabel}
      target={target}
      rel={rel}
      {...rest}
    >
      {children}
    </Link>
  );
};
